import React, { useState, useEffect } from 'react';
import { Link as RouterLink, Outlet, useLocation } from 'react-router-dom';
import {
    AppBar,
    Box,
    Drawer,
    Toolbar,
    IconButton,
    Typography,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Divider,
    Tooltip,
    useMediaQuery,
    useTheme
} from '@mui/material';
import {
    AccountCircle,
    Menu as MenuIcon,
    ViewModule,
    Extension,
    People,
    Person,
    Logout as LogoutIcon,
    Search as SearchIcon,
    Assessment as AssessmentIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';

const DRAWER_WIDTH = 240;
const DRAWER_WIDTH_COLLAPSED = 56;

export const Navigation: React.FC = () => {
    const { isAuthenticated, isAdmin, logout } = useAuth();
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [desktopCollapsed, setDesktopCollapsed] = useState(false);
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Close mobile drawer when route changes
    useEffect(() => {
        if (mobileOpen) {
            setMobileOpen(false);
        }
    }, [location.pathname]);

    // Load collapsed state from localStorage
    useEffect(() => {
        const saved = localStorage.getItem('nav-collapsed');
        if (saved === 'true') {
            setDesktopCollapsed(true);
        }
    }, []);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleDesktopCollapse = () => {
        const newState = !desktopCollapsed;
        setDesktopCollapsed(newState);
        localStorage.setItem('nav-collapsed', String(newState));
    };

    const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
        setMenuAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setMenuAnchorEl(null);
    };

    const navItems = [
        { text: 'Sets', icon: <ViewModule />, path: '/sets' },
        { text: 'Parts', icon: <Extension />, path: '/parts' },
        { text: 'Search', icon: <SearchIcon />, path: '/search' },
        { text: 'Reports', icon: <AssessmentIcon />, path: '/reports' },
    ];

    if (isAdmin) {
        navItems.push({ text: 'Users', icon: <People />, path: '/admin/users' });
    }

    const drawer = (collapsed: boolean = false) => (
        <div>
            <Toolbar sx={{ justifyContent: collapsed ? 'center' : 'space-between' }}>
                {!collapsed && (
                    <Typography variant="h6" noWrap component="div">
                        LEGO Manager
                    </Typography>
                )}
                {!isMobile && (
                    <IconButton onClick={handleDesktopCollapse} size="small">
                        {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                    </IconButton>
                )}
            </Toolbar>
            <Divider />
            <List>
                {navItems.map((item) => (
                    <Tooltip 
                        key={item.text}
                        title={collapsed ? item.text : ''} 
                        placement="right"
                        arrow
                    >
                        <ListItem 
                            button 
                            component={RouterLink} 
                            to={item.path}
                            selected={location.pathname === item.path}
                            sx={{
                                justifyContent: collapsed ? 'center' : 'flex-start',
                                px: collapsed ? 1 : 2,
                            }}
                        >
                            <ListItemIcon sx={{ 
                                minWidth: collapsed ? 0 : 56,
                                justifyContent: 'center'
                            }}>
                                {item.icon}
                            </ListItemIcon>
                            {!collapsed && <ListItemText primary={item.text} />}
                        </ListItem>
                    </Tooltip>
                ))}
            </List>
        </div>
    );

    if (!isAuthenticated) {
        return <Outlet />;
    }

    const currentDrawerWidth = desktopCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH;

    return (
        <Box sx={{ display: 'flex' }}>
            <AppBar
                position="fixed"
                sx={{
                    width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
                    ml: { sm: `${currentDrawerWidth}px` },
                    transition: theme.transitions.create(['margin', 'width'], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                    }),
                }}
            >
                <Toolbar>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2, display: { sm: 'none' } }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Box sx={{ flexGrow: 1 }} />
                    <IconButton
                        size="large"
                        aria-label="account of current user"
                        aria-controls="menu-appbar"
                        aria-haspopup="true"
                        onClick={handleMenu}
                        color="inherit"
                    >
                        <AccountCircle />
                    </IconButton>
                    <Menu
                        id="menu-appbar"
                        anchorEl={menuAnchorEl}
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        keepMounted
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        open={Boolean(menuAnchorEl)}
                        onClose={handleClose}
                    >
                        <MenuItem 
                            component={RouterLink} 
                            to="/profile"
                            onClick={handleClose}
                        >
                            <ListItemIcon>
                                <Person fontSize="small" />
                            </ListItemIcon>
                            Profile
                        </MenuItem>
                        <MenuItem onClick={() => {
                            handleClose();
                            logout();
                        }}>
                            <ListItemIcon>
                                <LogoutIcon fontSize="small" />
                            </ListItemIcon>
                            Logout
                        </MenuItem>
                    </Menu>
                </Toolbar>
            </AppBar>
            <Box
                component="nav"
                sx={{ 
                    width: { sm: currentDrawerWidth }, 
                    flexShrink: { sm: 0 },
                    transition: theme.transitions.create('width', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                    }),
                }}
            >
                {/* Mobile drawer */}
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true, // Better open performance on mobile.
                    }}
                    sx={{
                        display: { xs: 'block', sm: 'none' },
                        '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
                    }}
                >
                    {drawer(false)}
                </Drawer>
                {/* Desktop drawer */}
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: 'none', sm: 'block' },
                        '& .MuiDrawer-paper': { 
                            boxSizing: 'border-box', 
                            width: currentDrawerWidth,
                            transition: theme.transitions.create('width', {
                                easing: theme.transitions.easing.sharp,
                                duration: theme.transitions.duration.leavingScreen,
                            }),
                            overflowX: 'hidden',
                        },
                    }}
                    open
                >
                    {drawer(desktopCollapsed)}
                </Drawer>
            </Box>
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
                    marginTop: '64px',
                    transition: theme.transitions.create(['margin', 'width'], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                    }),
                }}
            >
                <Outlet />
            </Box>
        </Box>
    );
};
